import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { StudentProfileService } from '@repo/api-sdk';
import { Badge, PageLoader, PremiumPageHeader } from '@repo/ui';
import { useTranslation } from 'react-i18next';
import {
  BadgePercent,
  CheckCircle2,
  Copy,
  Gift,
  Share2,
  Sparkles,
  Target,
  Users,
  Wallet,
} from 'lucide-react';

const formatCurrency = (amount: number, lang: string) =>
  new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'en-US', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount ?? 0);

const ReferralRewardsPage = () => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['student-referral-rewards-page'],
    queryFn: () => StudentProfileService.getProfile(),
  });

  if (isLoading) {
    return <PageLoader message={t('common.loading')} />;
  }

  const studentRole =
    (profileData as any)?.studentRole ||
    (Array.isArray((profileData as any)?.roles)
      ? (profileData as any).roles.find(
          (roleObj: any) => String(roleObj?.role || '').toLowerCase() === 'student'
        )
      : null);

  const referralVoucher = (profileData as any)?.referralVoucher;
  const referralCode = referralVoucher?.code || '—';
  const isActive = Boolean(referralVoucher?.isActive);
  const referralBalance = Number(studentRole?.referralRewardBalance || 0);
  const referralPoints = Number(studentRole?.referralRewardPoints || 0);
  const successfulReferrals = Number(studentRole?.referralSuccessfulReferrals || 0);

  const handleCopyCode = async () => {
    if (!referralVoucher?.code) return;
    try {
      await navigator.clipboard.writeText(referralVoucher.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const handleShareCode = async () => {
    if (!referralVoucher?.code || typeof navigator === 'undefined' || !('share' in navigator)) {
      return;
    }

    const shareText = t('student.referrals.shareMessage', {
      defaultValue:
        'استخدم كود الدعوة {{code}} أثناء التسجيل في Route Academy.',
      code: referralVoucher.code,
    });

    try {
      await navigator.share({
        title: t('student.referrals.title'),
        text: shareText,
      });
    } catch {
      // Ignore canceled native share action.
    }
  };

  const metrics = [
    {
      key: 'balance',
      icon: Wallet,
      label: t('student.referrals.balance', { defaultValue: 'رصيد الدعوات' }),
      value: formatCurrency(referralBalance, i18n.language),
      tone: 'text-blue-700 dark:text-blue-300',
      bg: 'bg-blue-500/10 dark:bg-blue-400/15',
      border: 'border-blue-200/70 dark:border-blue-500/20',
    },
    {
      key: 'points',
      icon: Target,
      label: t('student.referrals.points', { defaultValue: 'نقاط الدعوات' }),
      value: referralPoints.toLocaleString(i18n.language === 'ar' ? 'ar-EG' : 'en-US'),
      tone: 'text-emerald-700 dark:text-emerald-300',
      bg: 'bg-emerald-500/10 dark:bg-emerald-400/15',
      border: 'border-emerald-200/70 dark:border-emerald-500/20',
    },
    {
      key: 'successful',
      icon: Users,
      label: t('student.referrals.successful', { defaultValue: 'الدعوات الناجحة' }),
      value: successfulReferrals.toLocaleString(i18n.language === 'ar' ? 'ar-EG' : 'en-US'),
      tone: 'text-slate-800 dark:text-slate-100',
      bg: 'bg-slate-500/10 dark:bg-white/10',
      border: 'border-slate-200/70 dark:border-white/10',
    },
  ];

  const infoCards = [
    {
      key: 'balanceMeaning',
      icon: Gift,
      title: t('student.referrals.balanceMeaningTitle', {
        defaultValue: 'ما معنى رصيد الدعوات؟',
      }),
      desc: t('student.referrals.balanceMeaningDesc', {
        defaultValue:
          'هذا هو إجمالي القيمة المالية التي حصلت عليها من الدعوات الناجحة، وتظهر لك بالجنيه المصري داخل حسابك.',
      }),
    },
    {
      key: 'pointsMeaning',
      icon: BadgePercent,
      title: t('student.referrals.pointsMeaningTitle', {
        defaultValue: 'ما معنى نقاط الدعوات؟',
      }),
      desc: t('student.referrals.pointsMeaningDesc', {
        defaultValue:
          'النقاط تمثل عدد المكافآت التي جمعتها من الدعوات، وتساعدك على متابعة نشاطك بسهولة.',
      }),
    },
    {
      key: 'successfulMeaning',
      icon: CheckCircle2,
      title: t('student.referrals.successfulMeaningTitle', {
        defaultValue: 'متى تُحسب دعوة ناجحة؟',
      }),
      desc: t('student.referrals.successfulMeaningDesc', {
        defaultValue:
          'تُحتسب عندما يستخدم الطالب الجديد كودك أثناء التسجيل ويتم اعتماد الدعوة داخل النظام.',
      }),
    },
  ];

  const steps = [
    t('student.referrals.stepOne', {
      defaultValue: 'شارك كود الدعوة الخاص بك مع طالب جديد.',
    }),
    t('student.referrals.stepTwo', {
      defaultValue: 'يقوم الطالب بإدخال الكود أثناء التسجيل.',
    }),
    t('student.referrals.stepThree', {
      defaultValue: 'بعد اعتماد الدعوة، يتم تحديث الرصيد والنقاط وعدد الدعوات الناجحة.',
    }),
  ];

  return (
    <div className="space-y-6 pb-6">
      <PremiumPageHeader
        title={t('student.referrals.title', { defaultValue: 'دعواتك' })}
        subtitle={t('student.referrals.subtitle', {
          defaultValue: 'اعرض كود الدعوة، وتابع الرصيد والنقاط وعدد الدعوات الناجحة.',
        })}
        icon={Gift}
        iconClassName="text-primary"
        iconContainerClassName="border-primary/20 bg-primary/10"
      />

      <section className="overflow-hidden rounded-[32px] border border-slate-200/80 bg-[radial-gradient(circle_at_top_right,_rgba(53,89,216,0.12),_transparent_28%),radial-gradient(circle_at_bottom_left,_rgba(16,185,129,0.12),_transparent_26%),linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.98))] p-4 shadow-sm dark:border-white/10 dark:bg-[radial-gradient(circle_at_top_right,_rgba(96,165,250,0.22),_transparent_28%),radial-gradient(circle_at_bottom_left,_rgba(52,211,153,0.14),_transparent_24%),linear-gradient(180deg,rgba(8,15,32,0.98),rgba(12,21,39,0.98))] dark:shadow-[0_20px_60px_-40px_rgba(37,99,235,0.45)] md:p-6">
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1.5 text-xs font-black text-primary ring-1 ring-inset ring-primary/10 dark:bg-white/10">
              <Sparkles className="h-3.5 w-3.5" />
              {t('student.referrals.heroBadge', {
                defaultValue: 'برنامج الدعوات',
              })}
            </div>

            <div className="space-y-3">
              <h2 className="max-w-2xl text-2xl font-black tracking-tight text-slate-950 dark:text-white md:text-4xl">
                {t('student.referrals.heroTitle', {
                  defaultValue: 'كود الدعوة ومكافآتك',
                })}
              </h2>
              <p className="max-w-2xl text-sm font-medium leading-7 text-slate-600 dark:text-slate-300 md:text-base">
                {t('student.referrals.heroDesc', {
                  defaultValue:
                    'من هنا يمكنك معرفة حالة كودك، وقيمة الرصيد، وعدد النقاط، وعدد الدعوات الناجحة.',
                })}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {metrics.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.key}
                    className={`rounded-[24px] border ${item.border} bg-white/75 p-4 backdrop-blur-sm dark:bg-white/[0.04] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]`}
                  >
                    <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${item.bg}`}>
                      <Icon className={`h-5 w-5 ${item.tone}`} strokeWidth={2.3} />
                    </div>
                    <p className="mt-4 text-xs font-black text-slate-500 dark:text-slate-400">
                      {item.label}
                    </p>
                    <p className={`mt-2 text-2xl font-black tracking-tight ${item.tone}`}>
                      {item.value}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-[28px] border border-slate-200/80 bg-white/85 p-4 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] md:p-5">
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-black text-slate-500 dark:text-slate-400">
                  {t('student.referrals.codeLabel', { defaultValue: 'كود الدعوة الخاص بك' })}
                </p>
              </div>
              <Badge
                className={`rounded-full border-transparent px-3 py-1 text-xs font-black ${
                  isActive
                    ? 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                    : 'bg-slate-500/10 text-slate-600 dark:bg-white/10 dark:text-slate-300'
                }`}
              >
                {isActive
                  ? t('common.active', { defaultValue: 'نشط' })
                  : t('common.inactive', { defaultValue: 'غير نشط' })}
              </Badge>
            </div>

            <div className="relative mt-4 overflow-hidden rounded-[26px] border border-[#b9d0ff] bg-[linear-gradient(135deg,#edf4ff_0%,#dceaff_54%,#cddfff_100%)] px-4 py-5 text-center shadow-[0_18px_40px_-28px_rgba(74,124,214,0.45)] dark:border-[#3559a8] dark:bg-[linear-gradient(135deg,#16294d_0%,#1c3460_52%,#234074_100%)] dark:shadow-[0_18px_50px_-26px_rgba(37,99,235,0.38)] sm:px-5 sm:py-6">
              <div className="absolute inset-y-0 left-0 w-20 bg-[radial-gradient(circle_at_left,_rgba(255,255,255,0.9),_transparent_70%)] dark:bg-[radial-gradient(circle_at_left,_rgba(191,219,254,0.22),_transparent_70%)] sm:w-24" />
              <div className="absolute -right-8 -top-8 h-20 w-20 rounded-full bg-white/45 blur-xl dark:bg-[#8ab4ff]/20 sm:h-24 sm:w-24" />
              <div className="absolute -left-4 bottom-2 h-14 w-14 rounded-full bg-[#9fc2ff]/25 blur-lg dark:bg-[#60a5fa]/18 sm:h-16 sm:w-16" />
              <div className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-2xl border border-white/60 bg-white/50 text-primary shadow-sm dark:border-white/15 dark:bg-white/10 dark:text-blue-200 sm:right-4 sm:top-4 sm:h-10 sm:w-10">
                <Gift className="h-5 w-5 sm:h-5 sm:w-5" strokeWidth={2.2} />
              </div>
              <div className="absolute left-3 top-3 rounded-full border border-white/60 bg-white/50 px-2.5 py-1 text-[9px] font-black tracking-[0.16em] text-primary/80 dark:border-white/15 dark:bg-white/10 dark:text-blue-100/80 sm:left-4 sm:top-4 sm:px-3 sm:text-[10px] sm:tracking-[0.18em]">
                REWARD
              </div>
              <p className="relative mt-8 break-all px-14 text-center font-mono text-[28px] font-black leading-none tracking-[0.03em] text-[#123c8d] dark:text-white sm:mt-6 sm:px-12 sm:text-[34px] md:text-[34px]">
                {referralCode}
              </p>
            </div>

            <p className="mt-4 text-center text-sm font-medium leading-7 text-slate-600 dark:text-slate-300 sm:text-start">
              {t('student.referrals.codeHelp', {
                defaultValue:
                  'شارك هذا الكود مع أي طالب جديد ليستخدمه أثناء التسجيل. عند اعتماد الدعوة سيتم تحديث مكافآتك هنا تلقائيًا.',
              })}
            </p>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={handleCopyCode}
                disabled={!referralVoucher?.code}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-800 transition-colors hover:border-primary/30 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-100 dark:hover:bg-white/[0.08]"
              >
                <Copy className="h-4 w-4" strokeWidth={2.4} />
                {copied
                  ? t('student.referrals.codeCopied', { defaultValue: 'تم نسخ الكود' })
                  : t('student.referrals.copyCode', { defaultValue: 'نسخ الكود' })}
              </button>

              {'share' in navigator && (
                <button
                  type="button"
                  onClick={handleShareCode}
                  disabled={!referralVoucher?.code}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3 text-sm font-black text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Share2 className="h-4 w-4" strokeWidth={2.4} />
                  {t('student.referrals.shareCode', { defaultValue: 'مشاركة الكود' })}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        {infoCards.map((card) => {
          const Icon = card.icon;

          return (
            <article
              key={card.key}
              className="rounded-[28px] border border-slate-200/70 bg-card p-5 shadow-sm transition-transform duration-300 hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </div>
              <h3 className="mt-4 text-lg font-black tracking-tight text-foreground">
                {card.title}
              </h3>
              <p className="mt-2 text-sm font-medium leading-7 text-muted-foreground">
                {card.desc}
              </p>
            </article>
          );
        })}
      </section>

      <section className="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="rounded-[28px] border border-slate-200/70 bg-card p-5 shadow-sm dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          <h3 className="text-xl font-black tracking-tight text-foreground">
            {t('student.referrals.stepsTitle', { defaultValue: 'كيف تستفيد من كود الدعوة؟' })}
          </h3>
          <div className="mt-5 space-y-4">
            {steps.map((step, index) => (
              <div key={step} className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-sm font-black text-white">
                  {index + 1}
                </div>
                <p className="pt-1 text-sm font-medium leading-7 text-muted-foreground">
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200/70 bg-card p-5 shadow-sm dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          <h3 className="text-xl font-black tracking-tight text-foreground">
            {t('student.referrals.noteTitle', { defaultValue: 'ما الذي ستفهمه من الأرقام هنا؟' })}
          </h3>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-[22px] border border-blue-200/70 bg-blue-50/80 p-4 dark:border-blue-500/20 dark:bg-blue-500/12">
              <p className="text-sm font-black text-blue-800 dark:text-blue-200">
                {t('student.referrals.noteBalanceTitle', { defaultValue: 'الرصيد' })}
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-blue-700/90 dark:text-blue-100/85">
                {t('student.referrals.noteBalanceDesc', {
                  defaultValue:
                    'رقم مالي ظاهر بالجنيه المصري يوضح قيمة المكافآت المتراكمة الناتجة عن دعواتك.',
                })}
              </p>
            </div>
            <div className="rounded-[22px] border border-emerald-200/70 bg-emerald-50/80 p-4 dark:border-emerald-500/20 dark:bg-emerald-500/12">
              <p className="text-sm font-black text-emerald-800 dark:text-emerald-200">
                {t('student.referrals.notePointsTitle', { defaultValue: 'النقاط' })}
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-emerald-700/90 dark:text-emerald-100/85">
                {t('student.referrals.notePointsDesc', {
                  defaultValue:
                    'عدد النقاط يساعدك على متابعة نشاطك في برنامج الدعوات بشكل سريع ومباشر.',
                })}
              </p>
            </div>
            <div className="rounded-[22px] border border-slate-200/70 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.05]">
              <p className="text-sm font-black text-slate-900 dark:text-white">
                {t('student.referrals.noteSuccessfulTitle', {
                  defaultValue: 'الدعوات الناجحة',
                })}
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-slate-600 dark:text-slate-300">
                {t('student.referrals.noteSuccessfulDesc', {
                  defaultValue:
                    'هذا الرقم يوضح كم مرة تم استخدام كودك بشكل صحيح واعتماد الإحالة في النظام.',
                })}
              </p>
            </div>
            <div className="rounded-[22px] border border-amber-200/70 bg-amber-50/80 p-4 dark:border-amber-500/20 dark:bg-amber-500/12">
              <p className="text-sm font-black text-amber-800 dark:text-amber-200">
                {t('student.referrals.noteStatusTitle', { defaultValue: 'حالة الكود' })}
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-amber-700/90 dark:text-amber-100/85">
                {t('student.referrals.noteStatusDesc', {
                  defaultValue:
                    'إذا كان الكود نشطًا يمكن مشاركته مباشرة. وإذا كان غير نشط فلن تُحتسب دعوات جديدة حتى يتم تفعيله.',
                })}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReferralRewardsPage;
